import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getActors, deleteActor, getActorPhotoUrl } from "@/services/firebase/actor";
import { Actor } from "@/types/actor";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import ActionMenu from "@/components/common/ActionMenu";
import { useDebounce } from "@/hooks/useDebounce";
import { artistAdminList, artistHubEnabled } from "@/services/firebase/artistHub";
import { PROFILE_STATUS_LABELS, VERIFIED_REASON_LABELS, type ArtistProfileStatus } from "@/lib/artistAdmin";
import {
  ARTIST_LIST_FILTERS,
  adminSearchText,
  artistSorters,
  countByFilter,
  filterArtists,
  mergeAdminRows,
  ownerLabel,
  profileStatusOf,
  type ArtistListFilter,
  type WithAdmin,
} from "@/lib/artistList";
import {
  AdminPage,
  AdminPageHeader,
  AdminSearchInput,
  AdminDataTable,
  AdminLoadingSkeleton,
  AdminEmptyState,
  AdminErrorAlert,
  AdminStatusBadge,
} from "@/components/admin-ui";
import type { AdminTableColumn } from "@/components/admin-ui/admin-data-table";

type ActorRow = WithAdmin<Actor>;

const PROFILE_TONES: Record<ArtistProfileStatus, "success" | "warning" | "neutral"> = {
  published: "success",
  unpublished: "warning",
  none: "neutral",
};

// 演員圖片組件 — 同步 build URL,瀏覽器自動 disk-cache
function ActorImage({ actor }: { actor: Actor }) {
  const photoUrl = getActorPhotoUrl(actor.photoURL);

  if (!photoUrl) {
    return (
      <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
        <UserCircle className="w-8 h-8 text-muted-foreground" />
      </div>
    );
  }

  return (
    <img
      src={photoUrl}
      alt={actor.name}
      width={64}
      height={64}
      loading="lazy"
      decoding="async"
      className="w-16 h-16 object-cover rounded-lg bg-muted"
    />
  );
}

const baseColumns: AdminTableColumn[] = [
  { key: "photo", label: "照片" },
  { key: "name", label: "名稱", sortable: true },
  { key: "gender", label: "性別", hideBelow: "md" },
  { key: "birthdate", label: "出生日期", hideBelow: "md", sortable: true },
  { key: "nationality", label: "國籍", hideBelow: "lg", sortable: true },
  { key: "createdAt", label: "建立時間", hideBelow: "lg", sortable: true },
  { key: "actions", label: "操作" },
];

/** artistHub-only columns, inserted after 名稱. */
const artistColumns: AdminTableColumn[] = [
  { key: "profile", label: "專頁", sortable: true, sortFn: artistSorters.profile },
  { key: "verified", label: "認證", hideBelow: "md", sortable: true, sortFn: artistSorters.verified },
  { key: "owners", label: "擁有者", hideBelow: "lg", sortable: true, sortFn: artistSorters.owners },
  { key: "followers", label: "追蹤", hideBelow: "md", sortable: true, sortFn: artistSorters.followers },
  { key: "works", label: "作品", hideBelow: "md", sortable: true, sortFn: artistSorters.works },
];

const withArtistColumns = (): AdminTableColumn[] => {
  const nameAt = baseColumns.findIndex((c) => c.key === "name") + 1;
  // 加入藝人欄位後隱藏生日／國籍以免表格過寬（仍可在編輯頁查看）。
  const rest = baseColumns.slice(nameAt).filter((c) => c.key !== "birthdate" && c.key !== "nationality");
  return [...baseColumns.slice(0, nameAt), ...artistColumns, ...rest];
};

export default function ActorListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    id: string | null;
    name: string;
  }>({ isOpen: false, id: null, name: "" });

  const { data: actors, isLoading, error } = useQuery({
    queryKey: ["actors"],
    queryFn: () => getActors(),
  });
  const [filter, setFilter] = useState<ArtistListFilter>("all");
  // One call for all artists (not per row); only where artistHub is deployed (sandbox).
  const adminList = useQuery({
    queryKey: ["artist-admin", "list"],
    queryFn: () => artistAdminList(),
    enabled: artistHubEnabled,
    retry: false,
    staleTime: 60000,
  });
  const showArtist = artistHubEnabled && !!adminList.data;
  const activeFilter: ArtistListFilter = showArtist ? filter : "all";
  const columns = useMemo(() => (showArtist ? withArtistColumns() : baseColumns), [showArtist]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteActor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["actors"] });
      setDeleteConfirm({ isOpen: false, id: null, name: "" });
      setDeleteError(null);
    },
    onError: (error) => {
      console.error("刪除演員失敗:", error);
      setDeleteError(error instanceof Error ? error.message : "未知錯誤");
      setDeleteConfirm({ isOpen: false, id: null, name: "" });
    },
  });

  const mergedActors = useMemo<ActorRow[]>(
    () => mergeAdminRows(actors ?? [], showArtist ? adminList.data?.items : undefined),
    [actors, showArtist, adminList.data],
  );
  const filterCounts = useMemo(() => countByFilter(mergedActors), [mergedActors]);

  // 過濾和搜尋
  const filteredActors = useMemo(() => {
    if (mergedActors.length === 0) return [];
    const searchLower = debouncedSearchTerm.toLowerCase();

    return filterArtists(mergedActors, activeFilter).filter((actor) => {
      const matchesSearch =
        !searchLower ||
        actor.id.toLowerCase().includes(searchLower) ||
        (actor.name && actor.name.toLowerCase().includes(searchLower)) ||
        (actor.nationality && actor.nationality.toLowerCase().includes(searchLower)) ||
        adminSearchText(actor.admin).includes(searchLower);

      return matchesSearch;
    });
  }, [mergedActors, debouncedSearchTerm, activeFilter]);

  const handleDelete = (id: string, name: string) => {
    setDeleteConfirm({ isOpen: true, id, name });
  };

  const confirmDelete = () => {
    if (deleteConfirm.id) {
      deleteMutation.mutate(deleteConfirm.id);
    }
  };

  const renderCell = (actor: ActorRow, column: AdminTableColumn) => {
    const admin = actor.admin;
    switch (column.key) {
      case "profile": {
        const status = profileStatusOf(admin);
        return <AdminStatusBadge label={PROFILE_STATUS_LABELS[status]} tone={PROFILE_TONES[status]} />;
      }
      case "verified":
        if (!admin || admin.profileStatus === "none") return <span className="text-sm text-muted-foreground">-</span>;
        return admin.verified ? (
          <AdminStatusBadge label="已認證" tone="purple" />
        ) : (
          <span className="text-xs text-muted-foreground">{VERIFIED_REASON_LABELS[admin.verifiedReason] ?? admin.verifiedReason}</span>
        );
      case "owners": {
        const first = ownerLabel(admin?.owners[0]);
        if (!admin || !first) return <span className="text-sm text-muted-foreground">-</span>;
        const more = admin.owners.length - 1;
        const firstOwner = admin.owners[0];
        return (
          <span className="text-sm text-foreground" title={admin.owners.map((o) => ownerLabel(o)).join("、")}>
            <span className={firstOwner.suspended ? "text-destructive line-through" : undefined}>{first}</span>
            {more > 0 && <span className="ml-1 text-xs text-muted-foreground">+{more}</span>}
          </span>
        );
      }
      case "followers":
        return (
          <span className="text-sm tabular-nums text-foreground">
            {admin ? admin.followerCount.toLocaleString("zh-TW") : "-"}
          </span>
        );
      case "works":
        if (!admin) return <span className="text-sm text-muted-foreground">-</span>;
        return (
          <span className="text-sm tabular-nums text-foreground">
            {admin.worksCount}
            {admin.unreleasedWorksCount > 0 && (
              <span className="ml-1 text-xs text-muted-foreground">+{admin.unreleasedWorksCount} 未上架</span>
            )}
          </span>
        );
      case "photo":
        return <ActorImage actor={actor} />;
      case "name":
        return (
          <span className="text-sm font-medium text-foreground">
            {actor.name || "-"}
          </span>
        );
      case "gender":
        return (
          <span className="text-sm text-muted-foreground">
            {actor.gender === "male"
              ? "男"
              : actor.gender === "female"
                ? "女"
                : actor.gender === "other"
                  ? "其他"
                  : "-"}
          </span>
        );
      case "birthdate":
        return (
          <span className="text-sm text-muted-foreground">
            {actor.birthdate
              ? new Date(actor.birthdate).toLocaleDateString("zh-TW")
              : "-"}
          </span>
        );
      case "nationality":
        return (
          <span className="text-sm text-muted-foreground">
            {actor.nationality || "-"}
          </span>
        );
      case "createdAt":
        return (
          <span className="text-sm text-muted-foreground">
            {actor.createdAt
              ? new Date(actor.createdAt).toLocaleString("zh-TW")
              : "-"}
          </span>
        );
      case "actions":
        return (
          <ActionMenu
            actions={[
              {
                label: "編輯",
                onClick: () => navigate(`/actors/${actor.id}/edit`),
              },
              {
                label: "刪除",
                onClick: () => handleDelete(actor.id, actor.name || actor.id),
                disabled: deleteMutation.isPending,
                variant: "danger",
              },
            ]}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <AdminPage>
        <AdminPageHeader title="藝人管理" createHref="/actors/new" createLabel="新增藝人" />
        <AdminLoadingSkeleton />
      </AdminPage>
    );
  }

  if (error) {
    return (
      <AdminPage>
        <AdminPageHeader title="藝人管理" createHref="/actors/new" createLabel="新增藝人" />
        <AdminErrorAlert
          message={`載入藝人時發生錯誤：${error instanceof Error ? error.message : "未知錯誤"}`}
        />
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      {deleteError && (
        <AdminErrorAlert
          message={`刪除藝人失敗：${deleteError}`}
          onDismiss={() => setDeleteError(null)}
        />
      )}

      <AdminPageHeader title="藝人管理" createHref="/actors/new" createLabel="新增藝人" />

      {artistHubEnabled && adminList.isError && (
        <AdminErrorAlert
          message={`無法載入藝人專頁狀態，列表僅顯示基本資料：${adminList.error instanceof Error ? adminList.error.message : "未知錯誤"}`}
        />
      )}

      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <AdminSearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={showArtist ? "搜尋名稱、ID、國籍、專頁名稱或擁有者..." : "搜尋演員名稱、ID 或國籍..."}
          className="max-w-md flex-1"
        />
        {showArtist && (
          <div className="flex flex-wrap gap-2" role="group" aria-label="藝人篩選">
            {ARTIST_LIST_FILTERS.map((f) => (
              <Button
                key={f.value}
                size="sm"
                variant={filter === f.value ? "secondary" : "ghost"}
                aria-pressed={filter === f.value}
                title={f.help}
                onClick={() => setFilter(f.value)}
              >
                {f.label}
                <span className="ml-1 text-xs tabular-nums text-muted-foreground">{filterCounts[f.value]}</span>
              </Button>
            ))}
          </div>
        )}
      </div>

      <AdminDataTable
        columns={columns}
        data={filteredActors}
        renderCell={renderCell}
        getRowId={(actor) => actor.id}
        emptyTitle={searchTerm || activeFilter !== "all" ? "沒有符合條件的藝人" : "尚無藝人資料"}
        emptyActionHref={searchTerm || activeFilter !== "all" ? undefined : "/actors/new"}
        emptyActionLabel={searchTerm || activeFilter !== "all" ? undefined : "新增第一位藝人"}
        emptyIcon={<UserCircle className="h-5 w-5" />}
        pageSize={20}
        defaultSort={{ key: "createdAt", dir: "desc" }}
      />

      {/* 刪除確認對話框 */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="確認刪除"
        message={`確定要刪除藝人「${deleteConfirm.name}」嗎？此操作無法復原。`}
        confirmText="刪除"
        cancelText="取消"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, name: "" })}
      />
    </AdminPage>
  );
}
